import React from 'react';
import { X, CheckCircle, Circle } from 'lucide-react';
import { PT } from './types';

interface PTDetailModalProps {
  pt: PT;
  onClose: () => void;
}

const PTDetailModal: React.FC<PTDetailModalProps> = ({ pt, onClose }) => {
  const sections = pt.section_progress || [];
  const teamMembers = pt.team_members || [];

  const getSectionEmoji = (percentage: number): string => {
    if (percentage === 100) return '✅';
    if (percentage >= 80) return '🟢';
    if (percentage >= 50) return '🟡';
    if (percentage >= 20) return '🟠';
    return '🔴';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">{pt.name}</h2>
              <p className="text-blue-100">Year: {pt.year} | NPWP: {pt.npwp}</p>
              <p className="text-blue-100">Risk Level: {pt.risk_level}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded transition"
            >
              <X size={24} />
            </button>
          </div>
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm">Overall Progress</span>
              <span className="text-2xl font-bold">{pt.progress_percentage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-white bg-opacity-20 rounded-full h-4">
              <div
                className="bg-white h-4 rounded-full transition-all"
                style={{ width: `${Math.min(pt.progress_percentage, 100)}%` }}
              />
            </div>
            <div className="text-sm text-blue-100 mt-1">
              {pt.complete_count} of {pt.total_tasks} tasks completed
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Team Members */}
          {teamMembers.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">👥 Audit Team</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {teamMembers.map((member, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
                    <div className="bg-blue-200 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-blue-700">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-600">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section Breakdown */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">📋 Audit Sections</h3>
            <div className="space-y-4">
              {sections.map((section, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-gray-50 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getSectionEmoji(section.percentage)}</span>
                        <div>
                          <h4 className="font-semibold text-gray-900">{section.name}</h4>
                          <p className="text-sm text-gray-600">
                            {section.complete} of {section.total} tasks completed
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          {section.percentage.toFixed(0)}%
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          section.percentage >= 80
                            ? 'bg-green-500'
                            : section.percentage >= 50
                            ? 'bg-yellow-500'
                            : section.percentage >= 20
                            ? 'bg-orange-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(section.percentage, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Task List */}
                  {section.tasks && section.tasks.length > 0 && (
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {section.tasks.map((task, taskIdx) => (
                          <div
                            key={taskIdx}
                            className={`flex items-start gap-2 p-2 rounded ${
                              task.status === 'complete' ? 'bg-green-50' : 'bg-gray-50'
                            }`}
                          >
                            {task.status === 'complete' ? (
                              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                            ) : (
                              <Circle className="text-gray-400 flex-shrink-0 mt-0.5" size={16} />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900">{task.code}</p>
                              <p className="text-xs text-gray-600 truncate" title={task.name}>
                                {task.name}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PTDetailModal;